import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Save, ArrowLeft } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { INDUSTRIES, FIJI_ISLANDS } from "@/lib/businesses";

export default function CreateBusiness() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        title: "",
        industry: "tourism_hospitality",
        location_island: "Viti Levu",
        location_region: "",
        public_description: "",
        private_description: "",
        year_established: "",
        employees: "",
        asking_price_fjd: "",
        annual_revenue_fjd: "",
        annual_ebitda_fjd: "",
        reason_for_sale: "",
        inventory_value_fjd: "",
        real_estate_included: false,
        external_photos: "",
    });
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

    const submit = async () => {
        if (!form.title || !form.public_description || !form.asking_price_fjd) {
            toast.error("Title, public description, and asking price are required.");
            return;
        }
        const payload = {
            ...form,
            year_established: form.year_established ? parseInt(form.year_established) : null,
            employees: form.employees ? parseInt(form.employees) : null,
            asking_price_fjd: parseFloat(form.asking_price_fjd),
            annual_revenue_fjd: form.annual_revenue_fjd ? parseFloat(form.annual_revenue_fjd) : null,
            annual_ebitda_fjd: form.annual_ebitda_fjd ? parseFloat(form.annual_ebitda_fjd) : null,
            inventory_value_fjd: form.inventory_value_fjd ? parseFloat(form.inventory_value_fjd) : null,
            external_photos: form.external_photos.split(",").map((s) => s.trim()).filter(Boolean),
        };
        // null-strip: backend has Optional fields, JSON null is fine
        setSubmitting(true);
        try {
            const { data } = await api.post("/businesses", payload);
            toast.success("Business listed!");
            navigate(`/businesses/${data.id}`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to create");
        } finally {
            setSubmitting(false);
        }
    };

    if (!user) return null;

    return (
        <div data-testid="create-business-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="mb-4">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <h1 className="font-display text-4xl font-bold mb-1">List your Fijian business</h1>
            <p className="text-muted-foreground font-app mb-8 max-w-2xl">
                Buyers see only your public summary. Revenue, EBITDA, and your full pitch are gated behind an NDA — buyers must request and you must approve before they see them.
            </p>

            <Card className="p-6 space-y-5">
                <div>
                    <Label>Listing title *</Label>
                    <Input data-testid="biz-title" value={form.title} onChange={set("title")} placeholder="Profitable beachside cafe — Pacific Harbour" className="mt-1.5" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <Label>Industry *</Label>
                        <Select value={form.industry} onValueChange={(v) => setForm((f) => ({ ...f, industry: v }))}>
                            <SelectTrigger data-testid="biz-industry" className="mt-1.5"><SelectValue /></SelectTrigger>
                            <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i.id} value={i.id}>{i.label}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Asking price (FJ$) *</Label>
                        <Input data-testid="biz-price" type="number" value={form.asking_price_fjd} onChange={set("asking_price_fjd")} placeholder="450000" className="mt-1.5" />
                    </div>
                    <div>
                        <Label>Island</Label>
                        <Select value={form.location_island} onValueChange={(v) => setForm((f) => ({ ...f, location_island: v }))}>
                            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                            <SelectContent>{FIJI_ISLANDS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Region / Town</Label>
                        <Input data-testid="biz-region" value={form.location_region} onChange={set("location_region")} placeholder="Pacific Harbour" className="mt-1.5" />
                    </div>
                </div>
                <div>
                    <Label>Public description *  <span className="text-muted-foreground font-normal text-xs">(visible to everyone — no sensitive numbers)</span></Label>
                    <Textarea data-testid="biz-public-desc" value={form.public_description} onChange={set("public_description")} rows={3} placeholder="Established cafe in a high-traffic resort precinct. Five-day-a-week operation, strong tripadvisor reviews." className="mt-1.5" />
                </div>

                <div className="border-t pt-5">
                    <h3 className="font-display text-lg font-semibold mb-1">NDA-gated — only shown after you approve a buyer's request</h3>
                    <p className="text-xs font-app text-muted-foreground mb-4">All five fields below stay hidden until you approve an NDA request from a verified buyer.</p>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Annual revenue (FJ$)</Label>
                            <Input data-testid="biz-revenue" type="number" value={form.annual_revenue_fjd} onChange={set("annual_revenue_fjd")} placeholder="380000" className="mt-1.5" />
                        </div>
                        <div>
                            <Label>Annual EBITDA (FJ$)</Label>
                            <Input data-testid="biz-ebitda" type="number" value={form.annual_ebitda_fjd} onChange={set("annual_ebitda_fjd")} placeholder="120000" className="mt-1.5" />
                        </div>
                        <div>
                            <Label>Year established</Label>
                            <Input type="number" value={form.year_established} onChange={set("year_established")} placeholder="2015" className="mt-1.5" />
                        </div>
                        <div>
                            <Label>Number of employees</Label>
                            <Input type="number" value={form.employees} onChange={set("employees")} placeholder="6" className="mt-1.5" />
                        </div>
                        <div className="sm:col-span-2">
                            <Label>Reason for sale</Label>
                            <Textarea value={form.reason_for_sale} onChange={set("reason_for_sale")} rows={2} placeholder="Owner relocating overseas." className="mt-1.5" />
                        </div>
                        <div className="sm:col-span-2">
                            <Label>Full pitch / private description</Label>
                            <Textarea data-testid="biz-private-desc" value={form.private_description} onChange={set("private_description")} rows={3} placeholder="Detailed financials, lease terms, growth opportunities…" className="mt-1.5" />
                        </div>
                    </div>
                </div>

                <div>
                    <Label>Photo URLs <span className="text-muted-foreground font-normal text-xs">(comma-separated)</span></Label>
                    <Input data-testid="biz-photos" value={form.external_photos} onChange={set("external_photos")} placeholder="https://images.unsplash.com/...,https://…" className="mt-1.5" />
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={form.real_estate_included} onCheckedChange={(v) => setForm((f) => ({ ...f, real_estate_included: !!v }))} data-testid="biz-real-estate" />
                    <span className="font-app text-sm">Real estate / freehold land included in the sale</span>
                </label>

                <div className="flex justify-end pt-2">
                    <Button onClick={submit} disabled={submitting} data-testid="biz-submit-btn" className="bg-ocean-green text-white">
                        <Save className="h-4 w-4 mr-2" /> {submitting ? "Saving…" : "Publish listing"}
                    </Button>
                </div>
            </Card>
        </div>
    );
}
